import assets from './stream-artwork.json'

interface ArtworkBounds { x: number; y: number; width: number; height: number }
interface ArtworkMetrics {
  width: number; height: number; bounds: ArtworkBounds
  anchor: { x: number; y: number }; motionBounds?: ArtworkBounds
}
const metrics: Partial<Record<string, ArtworkMetrics>> = assets

export function streamArtworkMetrics(name: string) {
  return metrics[name]
}

/** Size the visible body; keep the complete source canvas and its animation origins. */
export function streamActorStyle(name: string) {
  const asset = streamArtworkMetrics(name)
  if (!asset) return undefined
  const { bounds } = asset
  return {
    '--actor-aspect': String(bounds.width / bounds.height),
    '--actor-width-factor': String(bounds.width / Math.max(bounds.width, bounds.height)),
    '--actor-canvas-width': `${asset.width / bounds.width * 100}%`,
    '--actor-canvas-height': `${asset.height / bounds.height * 100}%`,
    '--actor-canvas-left': `${-bounds.x / bounds.width * 100}%`,
    '--actor-canvas-top': `${-bounds.y / bounds.height * 100}%`,
  }
}

/** Transparent canvas margins must not hide clipping of an animated character. */
export function streamSceneFits(root: HTMLElement): boolean {
  const primary = root.querySelector<HTMLElement>('[data-scene-primary]')
  const stage = primary?.closest<HTMLElement>('.daily-decoration')
  if (!primary || !stage || !primary.getClientRects().length) return true
  const asset = streamArtworkMetrics(primary.dataset.artworkName ?? '')
  if (!asset) return true
  const box = primary.getBoundingClientRect()
  const composition = primary.closest<HTMLElement>('.stream-decorations')
  const animated = composition?.dataset.motionActive === 'true' && !composition.dataset.feedback && composition.dataset.preview !== 'true'
  const bounds = animated ? asset.motionBounds ?? asset.bounds : asset.bounds
  const reference = primary.matches('img') ? { x: 0, y: 0, width: asset.width, height: asset.height } : asset.bounds
  const left = box.left + (bounds.x - reference.x) * box.width / reference.width
  const top = box.top + (bounds.y - reference.y) * box.height / reference.height
  const right = left + bounds.width * box.width / reference.width
  const bottom = top + bounds.height * box.height / reference.height
  // The decoration's footprint is the movement reservation, including open-sided scenes.
  const area = stage.getBoundingClientRect()
  if (left < area.left - 1 || right > area.right + 1 || top < area.top - 1 || bottom > area.bottom + 1) return false
  for (let parent = primary.parentElement; parent && parent !== root; parent = parent.parentElement) {
    const style = root.ownerDocument.defaultView!.getComputedStyle(parent)
    const clip = parent.getBoundingClientRect()
    if (style.overflowX === 'hidden' && (left < clip.left - 1 || right > clip.right + 1)) return false
    if (style.overflowY === 'hidden' && (top < clip.top - 1 || bottom > clip.bottom + 1)) return false
  }
  return true
}
